// src/pages/LegalPages.tsx
import React from 'react';
import { Shield, FileText, Cookie, AlertTriangle, RefreshCw } from 'lucide-react';

// Privacy Policy Page
export const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="flex items-center mb-8">
            <Shield className="h-8 w-8 text-blue-600 mr-3" />
            <h1 className="text-3xl font-bold text-gray-900">Privacy Policy</h1>
          </div>
          
          <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
            <p className="text-sm text-gray-500 mb-8">
              <strong>Effective Date:</strong> January 1, 2025
            </p>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Information We Collect</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium text-gray-900">Personal Information</h3>
                  <p>We collect information you provide directly, including:</p>
                  <ul className="list-disc list-inside ml-4 space-y-1">
                    <li>Name, email address, phone number</li>
                    <li>Property address and details</li>
                    <li>Financial situation details (mortgage information, payment status)</li>
                    <li>Communication preferences</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-medium text-gray-900">Automatically Collected Information</h3>
                  <ul className="list-disc list-inside ml-4 space-y-1">
                    <li>IP address and device information</li>
                    <li>Browser type and version</li>
                    <li>Pages visited and time spent</li>
                    <li>Referral sources</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">2. How We Use Your Information</h2>
              <ul className="list-disc list-inside space-y-2">
                <li><strong>Foreclosure Assistance:</strong> To provide personalized help and connect you with appropriate resources</li>
                <li><strong>Communication:</strong> To respond to inquiries and provide updates on your case</li>
                <li><strong>Legal Compliance:</strong> To comply with applicable laws and regulations</li>
                <li><strong>Service Improvement:</strong> To enhance our services and user experience</li>
                <li><strong>Marketing:</strong> To send relevant information about our services (with your consent)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">3. Information Sharing</h2>
              <p>We may share your information with:</p>
              <ul className="list-disc list-inside space-y-2">
                <li><strong>Service Providers:</strong> Third-party companies that help us provide our services</li>
                <li><strong>Legal Professionals:</strong> Attorneys and housing counselors who can assist with your situation</li>
                <li><strong>Real Estate Professionals:</strong> Licensed agents and investors who may provide solutions</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              </ul>
              <p className="mt-4"><strong>We never sell your personal information to third parties.</strong></p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Data Security</h2>
              <p>We implement industry-standard security measures including:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>SSL encryption for all data transmission</li>
                <li>Secure database storage with access controls</li>
                <li>Regular security audits and updates</li>
                <li>Employee training on data protection</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Your Rights</h2>
              <p>You have the right to:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Access your personal information</li>
                <li>Correct inaccurate information</li>
                <li>Request deletion of your information</li>
                <li>Opt-out of marketing communications</li>
                <li>File a complaint with relevant authorities</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Contact Information</h2>
              <p>For questions about this Privacy Policy, contact us at:</p>
              <div className="bg-gray-50 p-4 rounded-lg mt-4">
                <p><strong>RepMotivatedSeller</strong></p>
                <p>Email: privacy@repmotivatedseller.com</p>
                <p>Phone: 1-800-REP-HELP</p>
                <p>Address: [Your Business Address]</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

// Terms & Conditions Page
export const TermsConditionsPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="flex items-center mb-8">
            <FileText className="h-8 w-8 text-blue-600 mr-3" />
            <h1 className="text-3xl font-bold text-gray-900">Terms & Conditions</h1>
          </div>
          
          <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
            <p className="text-sm text-gray-500 mb-8">
              <strong>Effective Date:</strong> January 1, 2025
            </p>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <p>By accessing and using RepMotivatedSeller services, you agree to be bound by these Terms & Conditions. If you do not agree, please do not use our services.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Service Description</h2>
              <p>RepMotivatedSeller provides:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Foreclosure assistance and consultation services</li>
                <li>Connection with real estate professionals and attorneys</li>
                <li>Educational resources and market information</li>
                <li>Lead generation services for real estate professionals</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">3. User Responsibilities</h2>
              <p>Users agree to:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Provide accurate and truthful information</li>
                <li>Use services for lawful purposes only</li>
                <li>Respect intellectual property rights</li>
                <li>Not attempt to harm or disrupt our systems</li>
                <li>Maintain confidentiality of account credentials</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Professional Services Disclaimer</h2>
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg">
                <p className="text-yellow-800">
                  <strong>Important:</strong> RepMotivatedSeller is not a law firm and does not provide legal advice. 
                  We connect you with licensed professionals who can provide appropriate guidance for your situation.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Limitation of Liability</h2>
              <p>RepMotivatedSeller shall not be liable for:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Indirect, incidental, or consequential damages</li>
                <li>Actions or omissions of third-party service providers</li>
                <li>Technical failures or interruptions</li>
                <li>Investment or financial decisions made by users</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Termination</h2>
              <p>We reserve the right to terminate or suspend access to our services for violations of these terms or for any other reason at our discretion.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Changes to Terms</h2>
              <p>We may update these terms periodically. Changes will be posted on this page with an updated effective date.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Contact Information</h2>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p><strong>RepMotivatedSeller</strong></p>
                <p>Email: legal@repmotivatedseller.com</p>
                <p>Phone: 1-800-REP-HELP</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

// Cookies Policy Page
export const CookiesPolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="flex items-center mb-8">
            <Cookie className="h-8 w-8 text-blue-600 mr-3" />
            <h1 className="text-3xl font-bold text-gray-900">Cookies Policy</h1>
          </div>
          
          <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
            <p className="text-sm text-gray-500 mb-8">
              <strong>Effective Date:</strong> January 1, 2025
            </p>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">What Are Cookies?</h2>
              <p>Cookies are small text files stored on your device when you visit our website. They help us provide a better user experience and understand how our site is used.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Types of Cookies We Use</h2>
              
              <div className="space-y-4">
                <div className="border-l-4 border-green-400 pl-4">
                  <h3 className="font-medium text-gray-900">Essential Cookies</h3>
                  <p>Required for basic website functionality, including:</p>
                  <ul className="list-disc list-inside ml-4">
                    <li>Session management</li>
                    <li>Security features</li>
                    <li>Form submission</li>
                  </ul>
                </div>

                <div className="border-l-4 border-blue-400 pl-4">
                  <h3 className="font-medium text-gray-900">Analytics Cookies</h3>
                  <p>Help us understand site usage, including:</p>
                  <ul className="list-disc list-inside ml-4">
                    <li>Page views and traffic sources</li>
                    <li>User behavior patterns</li>
                    <li>Performance optimization</li>
                  </ul>
                </div>

                <div className="border-l-4 border-purple-400 pl-4">
                  <h3 className="font-medium text-gray-900">Marketing Cookies</h3>
                  <p>Used for personalized advertising and tracking:</p>
                  <ul className="list-disc list-inside ml-4">
                    <li>Social media integration</li>
                    <li>Ad targeting and retargeting</li>
                    <li>Conversion tracking</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Managing Cookies</h2>
              <p>You can control cookies through:</p>
              <ul className="list-disc list-inside space-y-1">
                <li><strong>Browser Settings:</strong> Most browsers allow you to block or delete cookies</li>
                <li><strong>Our Cookie Banner:</strong> Manage preferences when you first visit</li>
                <li><strong>Third-Party Tools:</strong> Opt-out of tracking through industry tools</li>
              </ul>
              
              <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg mt-4">
                <p className="text-amber-800">
                  <strong>Note:</strong> Disabling essential cookies may affect website functionality.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Third-Party Cookies</h2>
              <p>We work with trusted partners who may set cookies:</p>
              <ul className="list-disc list-inside space-y-1">
                <li><strong>Google Analytics:</strong> Website analytics and reporting</li>
                <li><strong>HubSpot:</strong> CRM and marketing automation</li>
                <li><strong>MailerLite:</strong> Email marketing services</li>
                <li><strong>Social Media:</strong> Facebook, LinkedIn, Twitter widgets</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Contact Us</h2>
              <p>Questions about our cookie usage? Contact us:</p>
              <div className="bg-gray-50 p-4 rounded-lg mt-4">
                <p>Email: privacy@repmotivatedseller.com</p>
                <p>Phone: 1-800-REP-HELP</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

// Disclaimer Page
export const DisclaimerPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="flex items-center mb-8">
            <AlertTriangle className="h-8 w-8 text-amber-600 mr-3" />
            <h1 className="text-3xl font-bold text-gray-900">Disclaimer</h1>
          </div>
          
          <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
            <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg mb-8">
              <p className="text-red-800 font-medium">
                <strong>Important Legal Notice:</strong> Please read this disclaimer carefully before using our services.
              </p>
            </div>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">No Attorney-Client Relationship</h2>
              <p>RepMotivatedSeller is not a law firm. Use of our services does not create an attorney-client relationship. We provide information and connections to licensed professionals, but do not provide legal advice.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">No Financial Advice</h2>
              <p>Information provided is for educational purposes only and should not be considered financial advice. Consult with qualified financial professionals before making investment or financial decisions.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Real Estate Transactions</h2>
              <p>We are not licensed real estate agents or brokers. All real estate transactions must be conducted through licensed professionals in accordance with state and federal laws.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">No Guarantee of Outcomes</h2>
              <p>We cannot guarantee specific results or outcomes. Each foreclosure situation is unique, and results depend on various factors beyond our control.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Information Accuracy</h2>
              <p>While we strive for accuracy, information on our site may become outdated. Always verify important information with qualified professionals and current sources.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Third-Party Services</h2>
              <p>We work with third-party service providers. We are not responsible for their actions, advice, or services. Users engage with third parties at their own risk.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Time-Sensitive Matters</h2>
              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                <p className="text-yellow-800">
                  <strong>Urgent Notice:</strong> Foreclosure proceedings are time-sensitive. Do not delay in seeking professional help. Contact an attorney immediately if you have received foreclosure notices.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Limitation of Liability</h2>
              <p>RepMotivatedSeller's liability is limited to the maximum extent permitted by law. We are not liable for any damages arising from use of our services.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Contact Information</h2>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p><strong>RepMotivatedSeller</strong></p>
                <p>Email: legal@repmotivatedseller.com</p>
                <p>Phone: 1-800-REP-HELP</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

// Return & Refund Policy Page
export const ReturnRefundPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="flex items-center mb-8">
            <RefreshCw className="h-8 w-8 text-green-600 mr-3" />
            <h1 className="text-3xl font-bold text-gray-900">Return & Refund Policy</h1>
          </div>
          
          <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
            <p className="text-sm text-gray-500 mb-8">
              <strong>Effective Date:</strong> January 1, 2025
            </p>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Our Commitment</h2>
              <p>RepMotivatedSeller is committed to providing valuable services. If you're not satisfied, we'll work to make it right.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Service Categories</h2>
              
              <div className="space-y-4">
                <div className="border border-green-200 p-4 rounded-lg">
                  <h3 className="font-medium text-gray-900 text-green-800">Free Services</h3>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Initial consultation and assessment</li>
                    <li>Educational resources and guides</li>
                    <li>Professional referrals and connections</li>
                  </ul>
                  <p className="text-sm text-green-700 mt-2">No refunds applicable - services are provided at no cost.</p>
                </div>

                <div className="border border-blue-200 p-4 rounded-lg">
                  <h3 className="font-medium text-gray-900 text-blue-800">Paid Services</h3>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Premium consultation packages</li>
                    <li>Document preparation services</li>
                    <li>Extended support programs</li>
                  </ul>
                  <p className="text-sm text-blue-700 mt-2">Refund policies apply as described below.</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Refund Eligibility</h2>
              <p>You may be eligible for a refund if:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Services were not delivered as promised</li>
                <li>You're not satisfied within the first 30 days</li>
                <li>Technical issues prevented service delivery</li>
                <li>Services were cancelled due to our inability to help</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Refund Process</h2>
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                <h3 className="font-medium text-blue-900">How to Request a Refund:</h3>
                <ol className="list-decimal list-inside space-y-1 mt-2">
                  <li>Contact us within 30 days of service purchase</li>
                  <li>Provide your order number and reason for refund</li>
                  <li>Allow 5-7 business days for review</li>
                  <li>Refunds processed within 10 business days if approved</li>
                </ol>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Non-Refundable Items</h2>
              <p>The following are not eligible for refunds:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Services already fully delivered and utilized</li>
                <li>Third-party fees (attorney fees, filing fees, etc.)</li>
                <li>Services requested after 30-day window</li>
                <li>Partial refunds for bundled services</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Satisfaction Guarantee</h2>
              <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                <p className="text-green-800">
                  <strong>Our Promise:</strong> If we can't help you within 30 days of engagement, 
                  we'll provide a full refund of any fees paid directly to RepMotivatedSeller.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Contact for Refunds</h2>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p><strong>Refund Requests:</strong></p>
                <p>Email: refunds@repmotivatedseller.com</p>
                <p>Phone: 1-800-REP-HELP (option 3)</p>
                <p>Business Hours: Monday-Friday, 9 AM - 6 PM EST</p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Dispute Resolution</h2>
              <p>If you're not satisfied with our refund decision, you may:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Request escalation to management</li>
                <li>Contact your payment processor</li>
                <li>Seek mediation through appropriate channels</li>
              </ul>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};